import React from 'react';
import { Link, useLocation } from 'react-router';
import logoLight from '../../../assets/logos/logo-light.svg';
import logoDark from '../../../assets/logos/logo-dark.svg';
import {
  Sidebar as SidebarUi,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
  SidebarSeparator,
} from '@repo/ui/Sidebar';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@repo/ui/Collapsible';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { useAccount } from '@/hooks/useAccount';
import { useAuthToken } from '@repo/hooks/useAuthToken';
import { buttonVariants } from '@repo/ui/Button';
import { CopyPasteField } from '@repo/ui/CopyPasteField';

export interface SidebarDataEntry {
  label: string;
  icon?: string;
  slug: string;
  isActive?: boolean;
  children?: SidebarDataEntry[];
}

const Sidebar = (props: React.ComponentProps<typeof SidebarUi>) => {
  const user = useUser();
  const currentAccount = useAccount();
  const currentAccountId = currentAccount?.id;
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const setToken = useAuthToken()[1];

  // Close the mobile sidebar on route change
  React.useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  const isParentViewingSubAccount =
    currentAccount?.parentAccount && user.account.id !== currentAccount.id;

  const isItemActive = (slug: string, children?: SidebarDataEntry[]) => {
    if (slug === `/a/${currentAccount?.id}`) {
      return location.pathname === slug;
    }

    // Direct match
    if (location.pathname === slug) return true;

    // Check if any child is active
    if (children?.some((child) => isItemActive(child.slug))) return true;

    // Check if current path starts with this slug (for nested routes)
    // Add trailing slash to ensure we match complete path segments
    if (location.pathname.startsWith(slug + '/')) {
      return true;
    }

    return false;
  };

  const SidebarData: (SidebarDataEntry | null)[] = [
    !currentAccount?.parentAccount
      ? {
          label: 'Home',
          icon: 'home',
          slug: `/a/${currentAccount?.id}`,
        }
      : null,

    POLICIES['api_keys:index'](user)
      ? {
          label: 'API',
          icon: 'api',
          slug: `/a/${currentAccount?.id}/develop`,
        }
      : null,

    POLICIES['dids:index'](user)
      ? {
          label: 'Numbers',
          icon: 'tag',
          slug: `/a/${currentAccount?.id}/numbers`,
        }
      : null,

    POLICIES['sms_requests:index'](user) || POLICIES['mms_requests:index'](user)
      ? {
          label: 'Messaging',
          icon: 'SMS',
          slug: `/a/${currentAccount?.id}/messaging`,
        }
      : null,

    POLICIES['messaging_auto_responses:index'](user) ||
    POLICIES['messaging_auto_responses:show'](user)
      ? {
          label: 'Auto responders',
          icon: 'quickreply',
          slug: `/a/${currentAccount?.id}/auto-responders`,
        }
      : null,

    POLICIES['csp_campaigns:index'](user) || POLICIES['csp_brands:index'](user)
      ? {
          label: 'TCR',
          icon: 'Campaign',
          slug: `/a/${currentAccount?.id}/tcr`,
          children: [
            { label: 'Brands', slug: `/a/${currentAccount?.id}/tcr/brands` },
            {
              label: 'Campaigns',
              slug: `/a/${currentAccount?.id}/tcr/campaigns`,
            },
          ],
        }
      : null,

    POLICIES['accounts:index'](user) && !currentAccount?.parentAccount
      ? {
          label: 'Sub Accounts',
          icon: 'account_circle',
          slug: `/a/${currentAccount?.id}/sub-accounts`,
        }
      : null,

    POLICIES['user:index'](user)
      ? {
          label: 'Users',
          icon: 'group',
          slug: `/a/${currentAccount?.id}/users`,
        }
      : null,

    !currentAccount?.parentAccount
      ? {
          label: 'Support',
          icon: 'support',
          slug: `/a/${currentAccount?.id}/support`,
        }
      : null,
  ];

  return (
    <SidebarUi {...props}>
      <SidebarContent className="md:border-r md:pt-20 border-grey-400 dark:border-grey-700">
        <SidebarHeader className="block md:hidden! border-b border-grey-400 dark:border-grey-700">
          <div className={`px-5 w-full duration-300`}>
            <Link to={`/a/${currentAccountId}`} className="dark:text-blue-100 max-w-[160px]">
              <img
                src={logoLight}
                alt="Swift SMS Gateway"
                width={140}
                height={75}
                className="block w-full dark:hidden max-w-[160px]"
              />
              <img
                src={logoDark}
                alt="Swift SMS Gateway"
                width={140}
                height={75}
                className="hidden w-full dark:block max-w-[160px]"
              />
            </Link>
          </div>
        </SidebarHeader>
        {isParentViewingSubAccount && (
          <SidebarGroup>
            <SidebarMenu className="flex flex-col">
              <SidebarMenuItem className="pt-2 pb-2">
                <div className="grid grid-cols-1 gap-2 items-center p-2 rounded-lg border divide-y divide-grey-400 border-grey-400 bg-grey-100 dark:bg-blue-900 dark:border-grey-600">
                  <div className="flex flex-col p-1">
                    <div className="text-xs text-grey-600 dark:text-grey-400">Sub Account:</div>
                    <div className="text-sm font-bold text-grey-600 dark:text-grey-300">{currentAccount?.name}</div>
                  </div>
                  <Link to={`/a/${user.account.id}`} className={buttonVariants({ variant: 'tertiary', size: 'sm' })}>
                    Back to Parent Account
                  </Link>
                </div>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
        <SidebarGroup>
          <SidebarMenu className="h-full">
            {SidebarData.filter(Boolean).map((item) => {
              const active = item ? isItemActive(item.slug, item.children) : false;

              if (item?.children) {
                return (
                  <Collapsible
                    key={item?.label}
                    asChild
                    defaultOpen={active}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          tooltip={item?.label}
                          className={active ? 'bg-primary-200  dark:bg-blue-900 ' : ''}
                        >
                          {item?.icon && (
                            <span className="material-symbols-outlined">{item?.icon}</span>
                          )}
                          <span>{item?.label}</span>
                          <span className="material-symbols-outlined ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90">
                            chevron_right
                          </span>
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item?.children?.map((subItem) => {
                            const subActive = location.pathname === subItem.slug;
                            return (
                              <SidebarMenuSubItem key={subItem.label}>
                                <SidebarMenuSubButton
                                  asChild
                                  className={subActive ? 'bg-blue-200  dark:bg-blue-800' : ''}
                                >
                                  <Link to={subItem.slug}>
                                    <span>{subItem.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              } else if (item?.slug && item?.label) {
                return (
                  <SidebarMenuItem key={item?.label}>
                    <SidebarMenuButton
                      tooltip={item?.label}
                      className={active ? 'bg-blue-200 dark:bg-blue-800 ' : ''}
                      asChild
                    >
                      <Link to={item?.slug} className="flex gap-2 items-center w-full">
                        {item?.icon && (
                          <span className="material-symbols-outlined">{item?.icon}</span>
                        )}
                        {item?.label}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }
              return null;
            })}
          </SidebarMenu>
        </SidebarGroup>

        {isMobile && (
          <>
            <SidebarSeparator className="mx-0 border-t border-grey-400" />


            <SidebarGroup>

            <SidebarMenuItem className="mb-4">
               <CopyPasteField label="Customer ID" text={currentAccount?.customerId?.toString() ?? 'No customer ID'} />
            </SidebarMenuItem>


              <SidebarMenu>
                {/* Account Dropdown */}
                <Collapsible asChild className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Account">
                        <span className="material-symbols-outlined">settings</span>
                        <span>Account</span>
                        <span className="material-symbols-outlined ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90">
                          chevron_right
                        </span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild>
                            <Link to={`/a/${currentAccountId}/account-settings`}>
                              <span>Manage Account</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        {POLICIES['account_allowed_access_ips:index'](user) && (
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton asChild>
                              <Link to={`/a/${currentAccountId}/account-settings/ip-whitelist`}>
                                <span>Security</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )}
                        {!currentAccount?.parentAccount?.id && (
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton asChild>
                              <Link to={`/a/${currentAccountId}/sub-accounts`}>
                                <span>Sub Accounts</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* User Dropdown */}
                <Collapsible asChild className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="User">
                        <span className="material-symbols-outlined">person</span>
                        <span>User</span>
                        <span className="material-symbols-outlined ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90">
                          chevron_right
                        </span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild>
                            <Link to={`/a/${currentAccountId}/user-settings/profile`}>
                              <span>User Settings</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setToken(null)}
                            className="cursor-pointer"
                          >
                            <span>Sign Out</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
    </SidebarUi>
  );
};

export default Sidebar;
